import { Chip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from '@material-ui/core';
import MyIcon from 'components/Icon';
import Truncate from 'components/Truncate';
import React from 'react';
import { useObservable } from 'react-use-observable';
import MyTableEmpty from 'v2/components/Shared/MyTable/MyTableEmpty';
import MyTableLoading from 'v2/components/Shared/MyTable/MyTableLoading';
import PaymentStatusTag from 'v2/components/Shared/PaymentStatusTag';
import PaymentTypeImg from 'v2/components/Shared/PaymentTypeImg';
import useWidth from 'v2/hooks/useWidth';
import { EPaymentMethod, type ShoppingDelivery } from 'v2/interfaces/shopping';
import shoppingService from 'v2/services/shopping/product';
import tableService from 'v2/services/table';

import useStyle from './styles';

const ProductList = ({ onClickRow }: { onClickRow: (shopping: ShoppingDelivery) => void }) => {
  const currentScreen = useWidth();
  const isMobile = currentScreen === 'xs' || currentScreen === 'sm';
  const classes = useStyle();
  const [isLoading] = useObservable(() => tableService.isLoading(), []);

  const [invoicesList] = useObservable(() => {
    return shoppingService.list();
  }, []);

  const [pagination] = useObservable(() => {
    return shoppingService.getPaginationResponse();
  }, []);

  const onChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const itemsPerPage = Number(e.target.value);
    shoppingService.setPagination({ ...pagination, ...{ itemsPerPage, page: 1 } });
  };

  const onChangePage = (_: unknown, newPage: number) => {
    const page = newPage + 1;
    shoppingService.setPagination({ ...pagination, ...{ page } });
  };

  const renderedList =
    invoicesList?.slice(
      (pagination?.page - 1) * pagination?.itemsPerPage,
      (pagination?.page - 1) * pagination?.itemsPerPage + pagination?.itemsPerPage
    ) || [];

  const getPaymentMethodName = (invoice: ShoppingDelivery) => {
    if (invoice.paymentMethod === EPaymentMethod.PAGAMENTO_COMBINADO) return 'Crédito + Pix';
    if (!!invoice.paymentMethodName) return invoice.paymentMethodName;
    return 'Desconhecido';
  };

  function getTableRow(delivery: ShoppingDelivery, index: number) {
    const getPaymentStatus = () => {
      return (
        <>
          <PaymentStatusTag delivery={delivery} />
          {delivery.product.physicalDelivery?.status ? (
            <Chip size='small' label={delivery.product.physicalDelivery.status} color='primary' variant='outlined' />
          ) : (
            ''
          )}
        </>
      );

      return '';
    };

    if (isMobile) {
      return (
        <div key={`shopping-${index}`} className={classes.mobileShoppingRow}>
          <div className={classes.productContainer}>
            <div className={classes.image} style={{ backgroundImage: `url(${delivery.product.image})` }} />

            <div className={classes.title}>
              <Truncate count={35} final='...'>
                {delivery.product.title}
              </Truncate>
              <span>
                contato: <a href={`mailto:${delivery.producer.email}`}>{delivery.producer.email ? delivery.producer.email : '-'}</a>
              </span>
            </div>
          </div>
          <div>{delivery.product.type}</div>

          <div>{getPaymentStatus()}</div>

          <div className={classes.paymentType}>
            <PaymentTypeImg delivery={delivery} />
            {getPaymentMethodName(delivery)}
          </div>

          <div className={classes.options}>
            <IconButton onClick={() => onClickRow(delivery)}>
              <MyIcon id='btn-options-shopping-table-row' name='icon-product-options' />
            </IconButton>
          </div>
        </div>
      );
    }

    return (
      <TableRow key={`shopping-${index}`} className={classes.tableRow}>
        <TableCell>
          <div className={classes.productContainer}>
            <div className={classes.image} style={{ backgroundImage: `url(${delivery.product.image})` }} />

            <div className={classes.title}>
              <Truncate count={35} final='...'>
                {delivery.product.title}
              </Truncate>
              <span>
                contato: <a href={`mailto:${delivery.producer.email}`}>{delivery.producer.email ? delivery.producer.email : '-'}</a>
              </span>
            </div>
          </div>
        </TableCell>
        <TableCell width={150}>{delivery.product.type}</TableCell>
        <TableCell width={300}>{getPaymentStatus()}</TableCell>
        <TableCell width={150}>
          <div className={classes.paymentType}>
            <PaymentTypeImg delivery={delivery} />
            {getPaymentMethodName(delivery)}
          </div>
        </TableCell>
        <TableCell align='right'>
          <div className={classes.options}>
            <IconButton onClick={() => onClickRow(delivery)}>
              <MyIcon id='btn-options-shopping-table-row' name='icon-product-options' />
            </IconButton>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {!isMobile && (
        <TableContainer>
          <Table id='products-table'>
            <TableHead>
              <TableRow className={classes.tableHead}>
                <TableCell>Produto</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Status</TableCell>
                <TableCell colSpan={2}>Forma de pagamento</TableCell>
              </TableRow>
            </TableHead>
            <MyTableLoading colSpan={4} />
            <TableBody className={classes.tableBody}>
              <MyTableEmpty data={renderedList} colSpan={4} />
              {!isLoading && renderedList.map((invoice, index) => getTableRow(invoice, index))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {isMobile && !isLoading && renderedList.map((invoice, index) => getTableRow(invoice, index))}

      {!isLoading && pagination && (
        <TablePagination
          labelRowsPerPage='Itens por página:'
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          rowsPerPageOptions={[10, 20, 50, 100]}
          component='div'
          count={pagination?.totalItems || 0}
          rowsPerPage={pagination?.itemsPerPage || 0}
          page={pagination?.page - 1 || 0}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />
      )}
    </>
  );
};

export default ProductList;
